/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Row, Col } from 'antd';

import { useArticle } from '@api/hooks';
import { NO_MARGIN_BOTTOM } from '@constants';
import useEventColors from '@hooks/useEventColors';
import { Typography } from '@atoms';
import { ArticleField, TextField, PositionField, ColorField, ModeSelect, ContentModuleModal } from '@molecules';
import MediaField from '@organisms/MediaField';
import { getImage, reСrop } from '@utils/media';

import Form from '../Form';

import { FORM_FIELD, FORM_FIELDS, MODE, MODES, INITIAL_VALUES } from './constants';
import Item from './Item';

const SinglePageFeature = ({
  visible,
  onSubmit,
  hide,
  previewImg,
  onDelete,
  initialValues: data = INITIAL_VALUES,
  errors,
  isUltimateDesign,
}) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const eventColors = useEventColors();

  const articleQuery = useArticle(data?.articleId);
  React.useEffect(() => {
    if (visible && articleQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.ARTICLE.name]: articleQuery.data });
    }
  }, [form, articleQuery.data, articleQuery.status, visible]);

  const [mode, setMode] = React.useState();
  React.useEffect(() => {
    if (visible) {
      setMode(data?.articleId ? MODE.EXISTING.value : MODE.MANUALLY.value);
    }
  }, [data, visible]);

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleOnValuesChange = ({ [FORM_FIELD.ARTICLE.name]: article }) => {
    if (article && mode === MODE.EXISTING.value) {
      const image = getImage(article, 'featureImage');
      const edit = reСrop(image.edited, FORM_FIELD.IMAGE.size);
      form.setFieldsValue({
        [FORM_FIELD.IMAGE.name]: { id: image.id, edited: edit },
      });
    }
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="Single page feature"
      subtitle="Choose a specific page to feature"
      okText={onDelete ? 'SAVE CHANGES' : 'ADD PAGE FEATURE'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form
        form={form}
        layout="vertical"
        wrapperCol={{ span: 24 }}
        initialValues={initialValues}
        onSerializedFinish={handleSubmit}
        onValuesChange={handleOnValuesChange}
      >
        <ModeSelect onSelect={setMode} mode={mode} modes={MODES} />
        {mode === MODE.MANUALLY.value && (
          <>
            <MediaField {...FORM_FIELD.IMAGE} />
            <TextField {...FORM_FIELD.TITLE} />
            <TextField {...FORM_FIELD.SUBTITLE} rules={isUltimateDesign ? [] : FORM_FIELD.SUBTITLE.rules} />
            {isUltimateDesign && <TextField {...FORM_FIELD.DESCRIPTION} />}
            <TextField {...FORM_FIELD.URL} />
            {isUltimateDesign && <TextField {...FORM_FIELD.URL_TEXT} />}
          </>
        )}
        {mode === MODE.EXISTING.value && <ArticleField {...FORM_FIELD.ARTICLE} />}
        {mode === MODE.EXISTING.value && <MediaField {...FORM_FIELD.IMAGE} />}
        {mode === MODE.EXISTING.value && isUltimateDesign && <TextField {...FORM_FIELD.DESCRIPTION} />}
        {mode === MODE.EXISTING.value && isUltimateDesign && <TextField {...FORM_FIELD.URL_TEXT} />}
        <Typography.Title level={3}>Settings</Typography.Title>
        <Row align="middle" gutter={20} style={{ marginBottom: 20 }}>
          <Col flex="0 0 50%">
            <PositionField {...FORM_FIELD.IMAGE_POSITION} />
          </Col>
          <Col flex="0 0 auto">
            <ColorField {...FORM_FIELD.BACKGROUND_COLOR} mergeColors={eventColors} style={NO_MARGIN_BOTTOM} />
          </Col>
        </Row>
      </Form>
    </ContentModuleModal>
  );
};

SinglePageFeature.Item = Item;

export default SinglePageFeature;
